import React, { useState } from "react";
import { Form } from "react-bootstrap";
import ProtectedButton from "../RBAC/ProtectedButton";

// 🔥 MASTER CONFIG (role → targets → sections → fields)
const FEEDBACK_CONFIG = {
  buyer: [
    {
      target: "seller",
      title: "Buyer → Seller",
      sections: [
        {
          title: "Quality & Compliance",
          fields: [{ key: "qualityCompliance", label: "Product/service met technical, quality, and certification requirements" }],
        },
        {
          title: "Communication & Responsiveness",
          fields: [{ key: "communicationResponsiveness", label: "Timely, clear, and professional interaction" }],
        },
        {
          title: "Delivery & Reliability",
          fields: [{ key: "deliveryReliability", label: "Adherence to agreed timelines, scope, and commitments" }],
        },
        {
          title: "Commercial Transparency",
          fields: [{ key: "commercialTransparency", label: "Clarity in pricing, documentation, and contractual alignment" }],
        },
      ],
    },
    {
      target: "platform",
      title: "Buyer → Platform",
      sections: [
        {
          title: "Supplier Match Quality",
          fields: [{ key: "supplierMatchQuality", label: "Relevance and capability alignment of recommended suppliers" }],
        },
        {
          title: "Platform Usability",
          fields: [{ key: "platformUsability", label: "Ease of managing RFQs, documents, and project workflows" }],
        },
        {
          title: "Issue Resolution Support",
          fields: [{ key: "issueResolution", label: "Effectiveness and speed of support when required" }],
        },
      ],
    },
    {
      target: "pm",
      title: "Buyer → Project Manager",
      sections: [
        {
          title: "Coordination Effectiveness",
          fields: [{ key: "coordinationEffectiveness", label: "Clarity in communication, follow-ups, and issue handling" }],
        },
      ],
    },
  ],

  seller: [
    {
      target: "buyer",
      title: "Seller → Buyer",
      sections: [
        {
          title: "Requirement Clarity",
          fields: [{ key: "requirementClarity", label: "Technical clarity of RFQ" }],
        },
        {
          title: "Communication",
          fields: [{ key: "buyerCommunication", label: "Buyer Responsiveness" }],
        },
        {
          title: "Commercial Discipline",
          fields: [{ key: "commercialDiscipline", label: "Payment terms clarity and adherence to commitments" }],
        },
      ],
    },
    {
      target: "platform",
      title: "Seller → Platform",
      sections: [
        {
          title: "Lead Quality",
          fields: [{ key: "leadQuality", label: "Relevance of buyer inquiries" }],
        },
        {
          title: "Platform Ease of Use",
          fields: [{ key: "platformEase", label: "Managing RFQs and submissions efficiently" }],
        },
        {
          title: "Workflow Support",
          fields: [{ key: "workflowSupport", label: "Milestone clarity, documentation flow, and tracking support" }],
        },
      ],
    },
    {
      target: "pm",
      title: "Seller → Project Manager",
      sections: [
        {
          title: "Coordination Effectiveness",
          fields: [{ key: "coordinationEffectiveness", label: "Clarity in communication, follow-ups, and issue handling" }],
        },
      ],
    },
  ],

  pm: [
    {
      target: "platform",
      title: "PM → Platform",
      sections: [
        {
          title: "Workflow Efficiency",
          fields: [{ key: "workflowEfficiency", label: "Project allocation and milestone monitoring" }],
        },
      ],
    },
    {
      target: "buyer",
      title: "PM → Buyer",
      sections: [
        {
          title: "Buyer Cooperation",
          fields: [{ key: "buyerCooperation", label: "Buyer Cooperation" }],
        },
      ],
    },
    {
      target: "seller",
      title: "PM → Seller",
      sections: [
        {
          title: "Seller Responsiveness",
          fields: [{ key: "sellerResponsiveness", label: "Seller Responsiveness" }],
        },
      ],
    },
    {
      target: "admin",
      title: "PM → Admin",
      sections: [
        {
          title: "Issue Resolution & Escalation",
          fields: [{ key: "issueResolution", label: "Issue Resolution & Escalation" }],
        },
      ],
    },
  ],
};

export default function FeedbackModal({ show, onClose, onSave, formType }) {
  const [reviewText, setReviewText] = useState("");
  const [ratings, setRatings] = useState({}); // { key: rating }

  const config = FEEDBACK_CONFIG[formType] || [];

  const allKeys = config.flatMap((group) =>
    group.sections.flatMap((section) => section.fields.map((f) => f.key))
  );

  const setRating = (key, value) => {
    setRatings((prev) => ({ ...prev, [key]: value }));
  };

  const canSave =
    reviewText.trim().length > 0 &&
    allKeys.every((key) => ratings[key] > 0);

  // 🔥 GROUPED PAYLOAD
  const buildPayload = () => {
    const groupedRatings = {};

    config.forEach((group) => {
      group.sections.forEach((section) => {
        section.fields.forEach((field) => {
          if (ratings[field.key]) {
            if (!groupedRatings[group.target]) {
              groupedRatings[group.target] = [];
            }

            groupedRatings[group.target].push({
              key: field.key,
              rating: ratings[field.key],
            });
          }
        });
      });
    });

    return {
      feedback: formType,
      reviewText,
      ratings: groupedRatings,
    };
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show d-block"
      style={{
        background: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(5px)",
        zIndex: 2000,
      }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        style={{ maxWidth: "750px", width: "95%" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content p-4 rounded-4">

          {/* Title */}
          <div style={{ fontWeight: 600, fontSize: "18px" }}>
            Feedback Form
          </div>

          {/* Review */}
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Write your overall feedback..."
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            className="mb-3"
          />

          {/* Rating scale */}
          <div style={{ fontSize: "13px", color: "#80829A", marginBottom: "10px" }}>
            1 = Poor | 2 = Below Expectations | 3 = Meets Expectations | 4 = Good | 5 = Excellent
          </div>

          {/* Dynamic Rendering */}
          {config.map((group) => (
            <div key={group.title} className="mb-4">
              <div style={{ fontWeight: 600, fontSize: "15px" }}>
                {group.title}
              </div>

              {group.sections.map((section) => (
                <div key={section.title} className="mt-2">
                  <div style={{ fontWeight: 500, fontSize: "14px" }}>
                    {section.title}
                  </div>

                  {section.fields.map((field) => (
                    <RatingRow
                      key={field.key}
                      field={field}
                      rating={ratings[field.key]}
                      onRate={setRating}
                    />
                  ))}
                </div>
              ))}
            </div>
          ))}

          {/* Button */}
          <div className="d-flex justify-content-end mt-3">
            <ProtectedButton
              permission="createFeedback"
              disabled={!canSave}
              onClick={() => onSave(buildPayload())}
              className="btn btn-dark"
            >
              Submit Feedback
            </ProtectedButton>
          </div>

        </div>
      </div>
    </div>
  );
}


// ⭐ Rating Row
function RatingRow({ field, rating = 0, onRate }) {
  return (
    <div className="d-flex justify-content-between align-items-center">
      <div style={{ fontSize: "14px" }}>{field.label}</div>
      <div>
        {[1, 2, 3, 4, 5].map((n) => (
          <span
            key={n}
            style={{
              fontSize: "20px",
              color: n <= rating ? "#fdbc56" : "#d2d2d2",
              cursor: "pointer",
            }}
            onClick={() => onRate(field.key, n)}
          >
            ★
          </span>
        ))}
      </div>
    </div>
  );
}
